// Clothes and hats. An outfit has two slots: `body` (clothes) and `head` (hats, which sit
// centered on top). Everything is drawn from the body's own landmarks, so it follows the body
// when it squashes, stretches, turns or tilts. Clothes cover the body's own pixels from the arm
// row down, so they fit any shape: Clawd's block, Chromie's ball, Foldi's folder.

export const OUTFITS = {
  body: { none: 'Nothing', suit: 'Suit and tie', hoodie: 'Hoodie', cape: 'Superhero cape', armor: 'Diamond armor', scarf: 'Scarf', chain: 'Gold chain' },
  head: { none: 'Nothing', tophat: 'Top hat', crown: 'Crown', party: 'Party hat', nightcap: 'Nightcap', fedora: 'Fedora' },
};

// How many rows a hat reaches above the top of the head it sits on (the room it needs on a web
// page), worked out from its own drawing.
const hatRows = new Map();
export function hatHeight(name, capDown = false) {
  if (!HATS[name]) return 0;
  const key = name + (capDown ? ':down' : '');
  if (!hatRows.has(key)) hatRows.set(key, -HATS[name]({ top: 0, L0: -4, R0: 3, put: () => {} }, { capDown }));
  return hatRows.get(key);
}

// Returns the highest row the outfit reaches (for placing a thought bubble above a hat).
export function drawOutfit(outfit, p, b) {
  if (!outfit) return b.top;
  CLOTHES[outfit.body]?.(withBody(b));
  const head = outfit.head;
  if (!head || head === 'none') return b.top;
  // A hat sits on the head: the whole top of the body, or the head the design marks (the
  // Wither's middle skull, the dragon's head between its wings). b.putHat, if given, draws it
  // (so the hat goes along when the head turns at the neck).
  return HATS[head] ? HATS[head]({ ...b, ...b.hat, put: b.putHat || b.put }, p) : b.top;
}

// ---------- clothes ----------
// Every clothes function gets the body's landmarks plus:
//   chest: the body's pixels from the arm row down, inBody(c, r): is (c, r) part of the body,
//   on(c, r, color): paint only where the body is, mid: [left, right] middle columns (or the
//   front column twice when turned, since the front is on the right before mirroring).
function withBody(b) {
  const cells = new Set(b.bodyPx.map(([c, r]) => c + ',' + r));
  const inBody = (c, r) => cells.has(c + ',' + r);
  const cl = Math.floor((b.L0 + b.R0) / 2), cr = Math.ceil((b.L0 + b.R0) / 2);
  return { ...b, inBody, chest: b.bodyPx.filter(([, r]) => r >= b.ar),
    on: (c, r, color) => { if (inBody(c, r)) b.put(c, r, color); },
    mid: b.side ? [b.R0 - 1, b.R0 - 1] : [cl, cr] };
}

const SUIT = { jacket: '#2E3038', lapel: '#3A3D47', shirt: '#F4F4F4', tie: '#C0392B', knot: '#E04B3C', trousers: '#25272E' };
const HOODIE = { main: '#5B6FD6', hood: '#4553B4', pocket: '#4A5CC4', string: '#F4F1EA' };
const CAPE = { out: '#D23A3A', in: '#8F2020', emblem: '#F2C230' };
const ARMOR = { light: '#B5FBF0', mid: '#4BD8CB', dark: '#2A9E96', legs: '#2F8F88' };
const SCARF = ['#D8343F', '#F4F1EA'];
const GOLD = { gold: '#F2C230', light: '#FFE07A', dark: '#B8891A' };

const CLOTHES = {
  suit({ put, on, chest, ar, bottom, side, R0, mid: [cl, cr], dressArms, dressLegs }) {
    for (const [c, r] of chest) put(c, r, SUIT.jacket);
    if (side) {                                   // turned: the tie and shirt show at the front
      on(R0 - 2, ar, SUIT.shirt); on(R0, ar, SUIT.shirt);
      for (let r = ar; r <= bottom; r++) on(R0 - 1, r, r === ar ? SUIT.knot : SUIT.tie);
    } else {
      on(cl - 1, ar, SUIT.shirt); on(cr + 1, ar, SUIT.shirt);
      on(cl - 2, ar, SUIT.lapel); on(cr + 2, ar, SUIT.lapel);
      for (let r = ar; r <= bottom; r++) { on(cl, r, r === ar ? SUIT.knot : SUIT.tie); if (cr !== cl) on(cr, r, r === ar ? SUIT.knot : SUIT.tie); }
    }
    dressArms(SUIT.jacket);                     // sleeves; the hands stay bare
    dressLegs(SUIT.trousers);
  },

  // A hoodie with the hood down on the shoulders, drawstrings and a front pocket.
  hoodie({ put, on, chest, ar, bottom, side, mid: [cl, cr], dressArms }) {
    for (const [c, r] of chest) put(c, r, r === ar ? HOODIE.hood : HOODIE.main);
    for (const c of side ? [cl] : [cl - 1, cr + 1]) { on(c, ar + 1, HOODIE.string); on(c, ar + 2, HOODIE.string); }
    if (!side && bottom - ar >= 4) for (let c = cl - 2; c <= cr + 2; c++) on(c, bottom - 1, HOODIE.pocket);
    dressArms(HOODIE.main);
  },

  // A cape behind the body, down to its feet, and an emblem on the chest.
  cape({ putUnder, on, ar, bottom, leg, side, L0, R0, mid: [cl, cr] }) {
    const low = bottom + leg;
    if (side) {                                   // it trails behind (the left before mirroring)
      for (let r = ar; r <= low; r++) { putUnder(L0 - 1, r, CAPE.out); if (r > ar + 1) putUnder(L0 - 2, r, CAPE.in); }
    } else {
      // All of it hangs behind the body: its edges show at the sides, its inside around a round body and between the legs.
      for (let r = ar; r <= low; r++) for (let c = L0 - 1; c <= R0 + 1; c++) putUnder(c, r, c < L0 || c > R0 ? CAPE.out : CAPE.in);
      for (const c of new Set([cl, cr])) { on(c, ar, CAPE.emblem); on(c, ar + 2, CAPE.emblem); }
      on(cl - 1, ar + 1, CAPE.emblem); on(cr + 1, ar + 1, CAPE.emblem);
      for (const c of new Set([cl, cr])) on(c, ar + 1, CAPE.in);
    }
  },

  // Minecraft diamond armor: a chestplate with glints, shoulder pads and leggings.
  armor({ put, inBody, chest, bottom, dressArms, dressLegs }) {
    for (const [c, r] of chest) {
      const edge = !inBody(c - 1, r) || !inBody(c + 1, r) || r === bottom;
      put(c, r, edge ? ARMOR.dark : (c + r) % 4 === 0 ? ARMOR.light : ARMOR.mid);
    }
    dressArms(ARMOR.mid);
    dressLegs(ARMOR.legs);
  },

  // A striped scarf around the arm row, its loose end hanging down at the front (no further
  // than just below the body, so it doesn't dangle between the legs of a short one).
  scarf({ put, chest, ar, bottom, side, R0, L0 }) {
    for (const [c, r] of chest) if (r === ar) put(c, r, SCARF[Math.floor((c + 40) / 2) % 2]);
    const end = side ? R0 - 1 : R0 - 2 - Math.floor((R0 - L0) / 6), length = Math.max(1, Math.min(3, bottom - ar));
    for (let k = 1; k <= length; k++) put(end, ar + k, SCARF[k % 2]);
    if (length >= 2) { put(end - 1, ar + length + 1, SCARF[0]); put(end + 1, ar + length + 1, SCARF[0]); }   // fringe
  },

  // A gold chain hanging in a U from the shoulders, with a medallion.
  chain({ on, ar, bottom, mid: [cl, cr] }) {
    const d = Math.max(0, Math.min(2, bottom - ar - 1));
    for (let k = 0; k < d; k++) { on(cl - 1 - d + k, ar + k, k % 2 ? GOLD.light : GOLD.gold); on(cr + 1 + d - k, ar + k, k % 2 ? GOLD.light : GOLD.gold); }
    for (let c = cl - 1; c <= cr + 1; c++) on(c, ar + d, (c + 40) % 2 ? GOLD.light : GOLD.gold);
    for (const c of new Set([cl, cr])) on(c, ar + d + 1, GOLD.light);
    if (cl === cr) { on(cl - 1, ar + d + 1, GOLD.dark); on(cr + 1, ar + d + 1, GOLD.dark); }
  },
};

// Each hat is a list of rows from the bottom up; row 0 sits right on top of the body.
// Columns are relative to the body's center.
function centered({ L0, R0 }) { return (L0 + R0 + 1) / 2; }
function rowAt(put, cx, r, from, to, color) { for (let c = Math.round(cx + from); c <= Math.round(cx + to); c++) put(c, r, color); }

const HATS = {
  tophat(b) {
    const cx = centered(b), t = b.top, black = '#1B1B1F', shine = '#3A3A42';
    rowAt(b.put, cx, t - 1, -5, 4, black);                       // brim
    rowAt(b.put, cx, t - 2, -3, 2, '#B8312F');                   // band
    for (let r = 3; r <= 5; r++) { rowAt(b.put, cx, t - r, -3, 2, black); b.put(Math.round(cx - 3), t - r, shine); }
    return t - 5;
  },
  crown(b) {
    const cx = centered(b), t = b.top, gold = '#F2C230', light = '#FFE07A', dark = '#C99A1C';
    rowAt(b.put, cx, t - 1, -3, 2, dark);
    rowAt(b.put, cx, t - 2, -3, 2, gold);
    b.put(Math.round(cx - 2), t - 2, '#D8343F'); b.put(Math.round(cx + 1), t - 2, '#3D86E0');   // jewels
    for (const c of [-3, -1, 0, 2]) b.put(Math.round(cx + c), t - 3, gold);
    for (const c of [-3, 2]) b.put(Math.round(cx + c), t - 4, light);
    return t - 4;
  },
  party(b) {
    const cx = centered(b), t = b.top, pink = '#FF5FA2', yellow = '#FFD23F';
    for (let c = -3; c <= 2; c++) b.put(Math.round(cx + c), t - 1, c % 2 ? pink : yellow);
    for (let c = -2; c <= 1; c++) b.put(Math.round(cx + c), t - 2, c % 2 ? yellow : pink);
    rowAt(b.put, cx, t - 3, -1, 0, pink);
    rowAt(b.put, cx, t - 4, -1, 0, '#FFFFFF');
    return t - 4;
  },
  fedora(b) {
    const cx = centered(b), t = b.top, felt = '#26272B', band = '#8A8F98', shine = '#3B3D42';
    rowAt(b.put, cx, t - 1, -5, 4, felt);                       // a wide brim
    rowAt(b.put, cx, t - 2, -3, 2, band);                       // the band
    rowAt(b.put, cx, t - 3, -3, 2, felt);
    rowAt(b.put, cx, t - 4, -2, 1, felt);                       // the pinched crown
    b.put(Math.round(cx - 3), t - 3, shine);
    return t - 4;
  },
  // A droopy nightcap. Pulled down (p.capDown), the brim covers the eyes.
  nightcap(b, p) {
    const cx = centered(b), t = b.top + (p.capDown ? 2 : 0), blue = '#3B5BDB', stripe = '#7A93EE', white = '#F4F1EA';
    for (let c = b.L0; c <= b.R0; c++) b.put(c, t - 1, white);   // brim, as wide as the body
    rowAt(b.put, cx, t - 2, -5, 3, blue);
    rowAt(b.put, cx, t - 3, -5, 1, stripe);
    rowAt(b.put, cx, t - 4, -6, -2, blue);
    rowAt(b.put, cx, t - 5, -7, -5, blue);                       // the tip flops over to the side
    rowAt(b.put, cx, t - 4, -9, -8, white); rowAt(b.put, cx, t - 5, -9, -8, white);   // pom-pom
    return t - 5;
  },
};
